
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Search, Menu, X, Mountain, Route } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import ThemeToggle from '@/components/ThemeToggle';
import { cn } from '@/lib/utils';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const isMobile = useIsMobile();

  // Track scroll position for navbar styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when changing routes
  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location]);

  // Track navigation events
  const trackNavigation = (destination: string) => {
    analytics.track('Navigation', {
      from: location.pathname,
      to: destination,
      mobileMenu: mobileMenuOpen
    });
  };

  // Track search
  const trackSearch = () => {
    analytics.track('Navbar Search Used', {
      location: 'navbar',
      isMobile: isMobile
    });
  };
  
  // Handle search submission
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      trackSearch();
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 dark:bg-black/80 backdrop-blur-lg shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link 
          to="/" 
          className="flex items-center space-x-2"
          onClick={() => trackNavigation('/')}
        >
          <div className="relative w-8 h-8 bg-nature-500 rounded-full flex items-center justify-center">
            <Mountain className="h-5 w-5 text-white" />
          </div>
          <span className="font-semibold text-lg tracking-tight">GoTrekking</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-8">
          <Link 
            to="/" 
            className="text-sm font-medium hover:text-nature-500 transition-colors"
            onClick={() => trackNavigation('/')}
          >
            Home
          </Link>
          <Link 
            to="/treks" 
            className="text-sm font-medium hover:text-nature-500 transition-colors"
            onClick={() => trackNavigation('/treks')}
          >
            Treks
            <Route className="inline-block ml-1.5 h-3.5 w-3.5 text-muted-foreground" />
          </Link>
          <Link 
            to="/companies" 
            className="text-sm font-medium hover:text-nature-500 transition-colors"
            onClick={() => trackNavigation('/companies')}
          >
            Companies
          </Link>
          
          {/* Theme Toggle */}
          <ThemeToggle />
          
          {/* Integrated search button in navbar */}
          <div className="relative">
            {searchOpen ? (
              <form onSubmit={handleSearchSubmit} className="flex items-center">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search treks..."
                  className="w-64 pl-3 pr-10 py-2 rounded-md bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-nature-500 text-sm"
                  autoFocus
                />
                <Button 
                  type="submit" 
                  size="icon" 
                  variant="ghost" 
                  className="absolute right-0 top-0 h-full"
                >
                  <Search className="h-4 w-4" />
                </Button>
              </form>
            ) : (
              <Button 
                variant="outline" 
                size="sm"
                className="flex items-center gap-2 text-sm"
                onClick={() => {
                  setSearchOpen(true);
                  analytics.track('Navbar Search Box Opened');
                }}
              >
                <Search className="h-3.5 w-3.5" />
                <span>Search</span>
              </Button>
            )}
          </div>
        </nav>

        {/* Mobile Menu */}
        <div className="flex md:hidden items-center gap-2">
          {/* Theme Toggle for Mobile */}
          <ThemeToggle />
          
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground"
            onClick={() => {
              analytics.track('Mobile Search Opened');
              navigate('/search');
            }}
          >
            <Search className="h-5 w-5" />
          </Button>
          
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => {
              setMobileMenuOpen(!mobileMenuOpen);
              analytics.track('Mobile Menu Toggle', { 
                action: mobileMenuOpen ? 'close' : 'open' 
              });
            }}
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden glass animate-fade-in border-t">
          <div className="container mx-auto px-4 py-4 space-y-4">
            <Link 
              to="/" 
              className="block py-2 text-sm font-medium hover:text-nature-500 transition-colors"
              onClick={() => trackNavigation('/')}
            >
              Home
            </Link>
            <Link 
              to="/treks" 
              className="block py-2 text-sm font-medium hover:text-nature-500 transition-colors"
              onClick={() => trackNavigation('/treks')}
            >
              Treks
              <Route className="inline-block ml-1.5 h-4 w-4 text-muted-foreground" />
            </Link>
            <Link 
              to="/companies" 
              className="block py-2 text-sm font-medium hover:text-nature-500 transition-colors"
              onClick={() => trackNavigation('/companies')}
            >
              Companies
            </Link>
            <div className="relative mt-4">
              <form onSubmit={(e) => {
                e.preventDefault();
                if (searchQuery.trim()) {
                  trackSearch();
                  navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
                  setMobileMenuOpen(false);
                }
              }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search treks..."
                  className="w-full py-2 pl-10 pr-4 rounded-full bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-nature-500/50"
                />
                <Search className="absolute top-2.5 left-3 h-4 w-4 text-muted-foreground" />
                <Button type="submit" className="sr-only">Search</Button>
              </form>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
